import api from './api';

/**
 * Posts blocked from viewing Assigned Queries, as configured server-side
 * (RESTRICTED_ASSIGNED_QUERIES_POSTS — see aai-backend/config/restrictedPosts.js).
 * Fetched once per app load and cached in memory + localStorage (so a value
 * is already available on the next load before the fetch resolves), read
 * synchronously wherever tab/route visibility is decided. The backend
 * enforces this independently on GET /queries/assigned — this cache only
 * drives UI visibility, so a stale or missing value fails open (tab shown)
 * rather than blocking anything.
 */
const STORAGE_KEY = 'restrictedAssignedQueriesPostBases';

let cachedPostBases = null;

const readStoredPostBases = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const storePostBases = (postBases) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(postBases));
  } catch {
    // Ignore storage errors (private browsing, quota, etc.)
  }
};

export const getRestrictedAssignedQueriesPostBases = () => {
  if (cachedPostBases === null) {
    cachedPostBases = readStoredPostBases();
  }
  return cachedPostBases;
};

export const isPostRestrictedFromAssignedQueries = (postShortNameBase) => {
  if (!postShortNameBase) return false;
  return getRestrictedAssignedQueriesPostBases().includes(postShortNameBase);
};

/**
 * Refreshes the cache from the server. Safe to call on every login/init;
 * failures are swallowed since this is a UI-visibility convenience, not the
 * access-control enforcement point.
 */
export const refreshRestrictedAssignedQueriesPostBases = async () => {
  try {
    const response = await api.get('/settings/restricted-assigned-queries-posts');
    const postBases = response?.data?.data?.postBases;
    if (Array.isArray(postBases)) {
      cachedPostBases = postBases;
      storePostBases(postBases);
    }
  } catch {
    // Keep whatever is already cached (or the empty default) — non-blocking.
  }
};
